import {SagaIterator} from 'redux-saga';
import {call, put, putResolve, select} from 'redux-saga/effects';

import {AMAZON_URL_PARAM} from 'desktop/pages/checkout/constants/amazon';
import {STEPS} from 'desktop/pages/checkout/constants/checkout';
import {getAllStates} from 'desktop/selectors/location/location';
import {AddressType} from 'desktop/types/addressType';
import {CustomerAddressType} from 'desktop/types/customerAddressesType';
import {apiConfig} from 'libs/api/api';
import {getUrlParams, getUrlSearchParam} from 'libs/url/url';
import {fetchStates} from 'reducers/location/location';

import {saveBillingAddress} from '../../../reducers/deliveryStep/billingAddress/billingAddress';
import {setAddressesSameness} from '../../../reducers/deliveryStep/billingAndShippingIsSame/billingAndShippingIsSame';
import {
    getCustomerAddresses as getCustomerAddressesAction,
    getCustomerAddressesFromOrder,
    setAddressLoaded,
} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {saveShippingAddress} from '../../../reducers/deliveryStep/shippingAddress/shippingAddress';
import {getOrderReviewTotals} from '../../../reducers/orderReviewTotals/orderReviewTotals';
import {changeStepStatus} from '../../../reducers/status/status';
import {getCustomerAddresses} from '../../../selectors/deliveryStep/deliveryStep';
import {getShippingAddress} from '../../../selectors/shippingAddress/shippingAddress';
import {decomposeAllAddressesByComponents} from '../decomposeAllAddressesByComponents/decomposeAllAddressesByComponents';
import {getShippingMethodsSaga} from '../getShippingMethodsSaga/getShippingMethodsSaga';
import {saveCustomerAddressSaga} from '../saveCustomerAddressSaga/saveCustomerAddressSaga';
/**
 * Saves received modified customer address and put them to store
 */
export function* putCustomerAddressesToStore({
    billingAddress,
    shippingAddress,
}: {
    shippingAddress: AddressType;
    billingAddress: AddressType;
}): SagaIterator {
    yield put(saveShippingAddress(shippingAddress));
    yield put(saveBillingAddress(billingAddress));
    yield put(setAddressLoaded(true));
}

/**
 * Requests customer addresses, prettify them and adds some additional information
 * @returns {SagaIterator} Prettified customer addresses
 */
export function* getCustomerAddressesSaga(): SagaIterator {
    const {
        payload: {
            data: {addresses, billing_address_id: billingID, shipping_address_id: shippingID},
        },
    } = yield putResolve(getCustomerAddressesAction());
    const shippingAddress = addresses.find(
        (address: CustomerAddressType) => address.addressId === shippingID,
    );
    const billingAddress = addresses.find(
        (address: CustomerAddressType) => address.addressId === billingID,
    );
    const addressesIsSame = shippingID === billingID;
    return {
        addresses,
        addressesIsSame,
        billingAddress,
        billingID,
        shippingAddress,
        shippingID,
    };
}

/**
 * Saga which gets saved customer addresses
 */
export function* getSavedCustomerAddressesSaga(): SagaIterator {
    const {addresses, addressesIsSame, billingAddress, billingID, shippingAddress, shippingID} =
        yield call(getCustomerAddressesSaga);

    if (!addresses.length || [shippingID, billingID].includes(null)) {
        yield put(setAddressLoaded(true));
        return;
    }

    const setAddressesSamenessAction = yield call(setAddressesSameness, addressesIsSame);
    yield put(setAddressesSamenessAction);

    const addressComponents = yield call(
        decomposeAllAddressesByComponents,
        shippingAddress,
        billingAddress,
        addressesIsSame,
    );
    if (addressComponents.shippingAddress && addressComponents.billingAddress) {
        yield call(putCustomerAddressesToStore, addressComponents);
    }
}

/**
 * Saga which gets saved customer addresses from order
 */
export function* getSavedCustomerAddressesFromOrderSaga(token: string): SagaIterator {
    const {
        payload: {
            data: {
                billing: billingAddress,
                same_addresses: isSameAddresses,
                shipping: shippingAddress,
            },
        },
    } = yield putResolve(getCustomerAddressesFromOrder(token));

    if (!shippingAddress) return;

    yield put(setAddressesSameness(isSameAddresses));

    const addressComponents = yield call(
        decomposeAllAddressesByComponents,
        shippingAddress,
        billingAddress,
        isSameAddresses,
    );
    if (addressComponents.shippingAddress && addressComponents.billingAddress) {
        yield call(putCustomerAddressesToStore, addressComponents);
    }
}

/**
 * Saga which gets saved customer addresses from account or from order
 */
export function* getSavedCustomerAddresses(): SagaIterator {
    const tokenFromUrl: string = yield call(getUrlSearchParam, 'token');
    const {is_logged_in: isLoggedIn} = apiConfig.hostStore.getState().customer;

    if (!isLoggedIn && tokenFromUrl) {
        yield call(getSavedCustomerAddressesFromOrderSaga, tokenFromUrl);
    } else {
        yield call(getSavedCustomerAddressesSaga);
    }
}

/**
 * Saga which gets states list for delivery step form (United States only)
 */
export function* getStatesSaga(): SagaIterator {
    const states = yield select(getAllStates);
    const {country} = yield select(getShippingAddress);

    if (
        // eslint-disable-next-line no-prototype-builtins
        !states.hasOwnProperty(country.value) ||
        states[country.value].length === 0
    ) {
        yield putResolve(fetchStates(country.value));
    }
}

/**
 * Finish preparation on delivery step
 * @param {boolean} isUserWithAddress - flag for customer with saved addresses
 */
export function* finishPreparationOnDeliveryStep(isUserWithAddress: boolean): SagaIterator {
    yield putResolve(getOrderReviewTotals({collectTax: isUserWithAddress, withTaxErrors: false}));

    yield put(
        changeStepStatus({
            [STEPS.DELIVERY_STEP]: {
                loaded: true,
            },
        }),
    );
}

/**
 * Check if is user with address and is not amazon to save address
 * @param {boolean} isUserWithAddress - flag for customer with saved addresses
 */
function* checkSaveCustomerAddress(isUserWithAddress: boolean): SagaIterator {
    const {search: searchParam} = apiConfig.hostStore.getState().router.location;
    const urlParams = yield call(getUrlParams, searchParam);
    const amazonPaymentConfirmed = urlParams[AMAZON_URL_PARAM.PAYMENT_CONFIRMED];

    if (isUserWithAddress && !amazonPaymentConfirmed) {
        yield call(saveCustomerAddressSaga);
    }
}

/**
 * Saga which start when delivery step component is mounted to page
 */
export function* deliveryStepSaga(): SagaIterator {
    yield call(getStatesSaga);

    yield call(getSavedCustomerAddresses);

    yield call(getShippingMethodsSaga);

    const customerAddresses = yield select(getCustomerAddresses);

    const isUserHasAddresses =
        customerAddresses.billing_address_id && customerAddresses.shipping_address_id;
    const {is_logged_in: isLoggedIn} = apiConfig.hostStore.getState().customer;

    const isUserWithAddress = isUserHasAddresses && isLoggedIn;
    yield call(checkSaveCustomerAddress, isUserWithAddress);
    yield call(finishPreparationOnDeliveryStep, isUserWithAddress);
}
